import React, { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import HelpIcon from '@mui/icons-material/Help';
import { Box, Grid, Skeleton, Tooltip, Typography } from '@mui/material';

import { StatNotAvailableReason } from './PromotionUpliftStats';

export interface PromotionUpliftStatsFCProps {
  strings: {
    weeklySpend: string;
    weeklyImpact: string;
    impactedUsersText: string;
    impactedGMVText: string;
    weeklyImpactTipText: string;
    statsNotAvailableStrings: Record<
      StatNotAvailableReason,
      {
        title: string;
        subtitle: string;
      }
    >;
  };
  upliftStats?: {
    weeklySpend?: {
      currency: string;
      value: number;
    };
    weeklyImpact?: {
      currency: string;
      gmvValue: number;
    };
    newUsers?: number;
  };
  showUpliftStatsSkeletons: boolean;
  isStatAvailable?: boolean;
  statNotAvailableReason?: StatNotAvailableReason;
}

interface StatProps {
  label: string;
  value: ReactNode;
  hasToolTip?: boolean;
  tipText?: string;
  showSkeleton?: boolean;
}

const style = {
  statBox: {
    padding: '24px 24px 8px 24px',
    background: '#F4F5F6',
    borderRadius: '16px',
  },
  stat: {
    padding: '0px 0px 16px 0px',
  },
  statTitle: {
    color: '#747A82',
    fontWeight: 600,
    fontFamily: 'euclidCircularB',
  },
  statSubtitle: {
    color: '#747A82',
    fontWeight: 400,
    fontFamily: 'euclidCircularB',
    padding: '8px 0px 16px 0px',
  },
  growthStats: {
    color: '#32BB78',
  },
} as const;

const DEFAULT_CURRENCY = 'EUR';

const displaySkeletonPlaceholder = (rows: number = 1, { color }: { color?: string } = {}) => {
  return Array(rows).fill(
    <Skeleton
      sx={{ bgcolor: color }}
      variant="text"
      width="132px"
      height="16px"
      animation="wave"
    />,
  );
};

const Stat = ({ label, value, hasToolTip, tipText, showSkeleton = false }: StatProps) => (
  <Box sx={style.stat} gap={1}>
    <Grid container direction="row" width="inherit" alignItems="center">
      <Grid item xs={hasToolTip ? 10 : 12}>
        <Typography variant="subtitleS" sx={style.statTitle} paragraph>
          {label}
        </Typography>
      </Grid>
      {hasToolTip && (
        <Grid item paddingLeft="11px" xs={2}>
          <Tooltip
            title={<Typography variant="inherit">{tipText}</Typography>}
            arrow
            placement="bottom-end"
          >
            <HelpIcon />
          </Tooltip>
        </Grid>
      )}
    </Grid>
    <Typography variant="title">{showSkeleton ? displaySkeletonPlaceholder() : value}</Typography>
  </Box>
);

const NoStatsAvailable = ({ statTitle, statSubtitle }) => (
  <Box gap={1}>
    <Typography sx={style.statTitle}>{statTitle}</Typography>
    <Typography sx={style.statSubtitle}>{statSubtitle}</Typography>
  </Box>
);
export const PromotionUpliftStatsFC = ({
  strings,
  upliftStats,
  showUpliftStatsSkeletons,
  isStatAvailable,
  statNotAvailableReason,
}: PromotionUpliftStatsFCProps) => {
  const intl = useIntl();
  const weeklySpend = upliftStats?.weeklySpend;
  const weeklyImpact = upliftStats?.weeklyImpact;
  const spendAmount = useMemo(
    () =>
      intl.formatNumber(weeklySpend?.value ?? 0, {
        currency: weeklySpend?.currency.trim() || DEFAULT_CURRENCY,
        style: 'currency',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [weeklySpend, intl],
  );
  const appliedstatNotAvailableReason: StatNotAvailableReason = useMemo(() => {
    if (!isStatAvailable) {
      if (statNotAvailableReason === StatNotAvailableReason.Bolt_Plus_Launch_Test) {
        return StatNotAvailableReason.Bolt_Plus_Launch_Test;
      }
    }
    return StatNotAvailableReason.Other;
  }, [isStatAvailable, statNotAvailableReason]);

  const weeklyImpactContent = useMemo(() => {
    if (showUpliftStatsSkeletons) {
      return displaySkeletonPlaceholder(2, { color: '#A1F7CD' });
    }
    const { currency, gmvValue } = weeklyImpact ?? {};
    const subDisplayStats = [
      {
        label: strings.impactedGMVText,
        value: intl.formatNumber(gmvValue ?? 0, {
          currency: currency?.trim() || DEFAULT_CURRENCY,
          style: 'currency',
          signDisplay: 'exceptZero',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
      },
    ];
    return (
      <Box sx={style.stat} gap={1}>
        {subDisplayStats.map(({ label, value }) => {
          return (
            <Typography variant="title" sx={style.growthStats}>
              {value}
              <br />
              <Typography variant="subtitleS" component="span" sx={style.growthStats}>
                {` ${label}`}
              </Typography>
            </Typography>
          );
        })}
      </Box>
    );
  }, [showUpliftStatsSkeletons, weeklyImpact, strings.impactedGMVText, intl]);
  const displayStats = [
    {
      label: strings.weeklySpend,
      value: spendAmount,
      showSkeleton: showUpliftStatsSkeletons,
    },
    {
      label: strings.weeklyImpact,
      value: weeklyImpactContent,
      showSkeleton: false,
      hasToolTip: true,
      tipText: strings.weeklyImpactTipText,
    },
  ];
  return (
    <Box sx={style.statBox} gap={2}>
      {isStatAvailable ? (
        displayStats.map((stat) => <Stat {...stat} />)
      ) : (
        <NoStatsAvailable
          statTitle={strings.statsNotAvailableStrings[appliedstatNotAvailableReason].title}
          statSubtitle={strings.statsNotAvailableStrings[appliedstatNotAvailableReason].subtitle}
        />
      )}
    </Box>
  );
};
